import { parseISO } from "date-fns";
import { isDocumentExpired } from "../DocumentDetailsRightSidebar/utils";
import { ComplianceDocumentType } from "./types";

/**
 * Gets the secondary document type (subtype) from document metadata.
 * The subtype is stored under the "type" key in the metadata array.
 */
const getSecondaryDocumentType = (
  metadata: ComplianceDocumentType["metadata"],
): string | undefined => {
  return metadata?.find((item) => item?.key === "type")?.value ?? undefined;
};

/**
 * Gets the date field value from document metadata.
 * For ID documents, the expiry is stored under "expiry" key.
 * For other documents (AML reliance letter, address verification), it's under "date" key.
 */
const getDateFromMetadata = (
  metadata: ComplianceDocumentType["metadata"],
): Date | undefined => {
  const expiryValue = metadata?.find((item) => item?.key === "expiry")?.value;
  if (expiryValue) return parseISO(expiryValue);

  const dateValue = metadata?.find((item) => item?.key === "date")?.value;
  if (dateValue) return parseISO(dateValue);

  return undefined;
};

/**
 * Determines if a document should show an "Expired" badge in the document list.
 * Reuses the existing isDocumentExpired utility, passing the same date as both
 * current and initial since the list view always displays backend data.
 */
export const isDocumentExpiredInList = (document: ComplianceDocumentType): boolean => {
  const dateFromMetadata = getDateFromMetadata(document.metadata);
  const documentExpiryDate = document.expiryDate ? parseISO(document.expiryDate) : undefined;
  const secondaryDocumentType = getSecondaryDocumentType(document.metadata);

  return isDocumentExpired(
    dateFromMetadata,
    dateFromMetadata, // same as current since list shows backend data
    documentExpiryDate,
    secondaryDocumentType,
  );
};
